import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import { category, finiteNumber, uniqueCategories } from './chartData'
import {
  buildChartTooltip,
  chartFieldLabel,
  formatChartAxisValue,
  formatChartValue,
} from './chartPresentation'
import { firstField, manyFields, type ResolvedChartFields } from './resolveChartFields'

/** Renderer default for callers without resolved catalog presentation. */
const DEFAULT_MAX_METRIC_PANELS = 5
const PANEL_GAP_PERCENT = 4

/** Highcharts axis `left`/`width` accept a percentage string; two decimals is ample precision. */
function percent(value: number): string {
  return `${Math.round(value * 100) / 100}%`
}

interface PointContext {
  color?: unknown
  y?: unknown
  point: { name?: unknown; category?: unknown; low?: unknown; high?: unknown; target?: unknown }
  series: { name: string; userOptions?: { custom?: { metric?: string } } }
}

function axisLabels(input: BuildCategoricalChartOptionsInput, metric: string) {
  return {
    formatter(this: { value: number }) {
      return formatChartAxisValue(input, Number(this.value), metric)
    },
  }
}

/**
 * Multi Metric: one horizontal panel per metric over a shared category axis. Each panel owns a
 * value axis so metrics with unrelated magnitudes (cost vs CTR) stay individually readable.
 */
function buildMultiMetric(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const dimension = firstField(fields, ['dimensions', 'dimension'])
  const metrics = manyFields(fields, ['metrics'])
  const maxPanels = input.presentation?.maxSmallMultiples ?? DEFAULT_MAX_METRIC_PANELS
  if (!dimension || metrics.length === 0 || metrics.length > maxPanels) return null

  const categories = input.rows.map((row) => category(row[dimension]))
  const width = (100 - PANEL_GAP_PERCENT * (metrics.length - 1)) / metrics.length
  return {
    chart: { type: 'bar', height: Math.max(360, categories.length * 28 + 120) },
    title: { text: '' },
    xAxis: { categories, lineWidth: 0, tickWidth: 0 },
    yAxis: metrics.map((metric, index) => ({
      left: percent(index * (width + PANEL_GAP_PERCENT)),
      width: percent(width),
      offset: 0,
      title: { text: chartFieldLabel(input, metric), style: { fontWeight: '700' } },
      labels: axisLabels(input, metric),
    })),
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      outside: true,
      formatter() {
        const context = this as unknown as PointContext
        const metric = context.series.userOptions?.custom?.metric ?? metrics[0]!
        return buildChartTooltip(context.point.category, [
          {
            color: context.color,
            label: context.series.name,
            value: formatChartValue(input, context.y, metric),
          },
        ])
      },
    },
    plotOptions: { bar: { borderRadius: 4, maxPointWidth: 24 } },
    series: metrics.map((metric, index) => ({
      type: 'bar',
      name: chartFieldLabel(input, metric),
      yAxis: index,
      colorIndex: index,
      data: input.rows.map((row) => finiteNumber(row[metric])),
      custom: { metric },
    })),
    credits: { enabled: false },
  } as CategoricalChartOptions
}

/** Dumbbell: previous-period (`prev_<metric>`) and current value joined per category. */
function buildDumbbell(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const dimension = firstField(fields, ['dimension'])
  const metric = firstField(fields, ['metric', 'metrics'])
  if (!dimension || !metric) return null
  const categories = input.rows.map((row) => category(row[dimension]))
  return {
    chart: { type: 'dumbbell', inverted: true },
    title: { text: '' },
    xAxis: { categories, lineWidth: 0, tickWidth: 0 },
    yAxis: { title: { text: chartFieldLabel(input, metric) }, labels: axisLabels(input, metric) },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      outside: true,
      formatter() {
        const context = this as unknown as PointContext
        return buildChartTooltip(context.point.category, [
          {
            color: 'var(--ui-chart-compare-previous)',
            label: 'Previous',
            value: formatChartValue(input, context.point.low, metric),
          },
          {
            color: 'var(--ui-chart-compare-actual)',
            label: 'Actual',
            value: formatChartValue(input, context.point.high, metric),
          },
        ])
      },
    },
    series: [
      {
        type: 'dumbbell',
        name: chartFieldLabel(input, metric),
        lowColor: 'var(--ui-chart-compare-previous)',
        color: 'var(--ui-chart-compare-actual)',
        data: input.rows.map((row) => ({
          low: finiteNumber(row[`prev_${metric}`]),
          high: finiteNumber(row[metric]),
        })),
      },
    ],
    credits: { enabled: false },
  } as CategoricalChartOptions
}

/** Bullet: actual value against its target, one bar per KPI label (or a single unlabeled bar). */
function buildBullet(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const metric = firstField(fields, ['metric'])
  const target = firstField(fields, ['target'])
  if (!metric || !target || input.rows.length === 0) return null
  const dimension = firstField(fields, ['dimension'])
  const categories = input.rows.map((row) =>
    dimension ? category(row[dimension]) : chartFieldLabel(input, metric),
  )
  return {
    chart: { type: 'bullet', inverted: true, height: Math.max(160, categories.length * 64 + 80) },
    title: { text: '' },
    xAxis: { categories },
    yAxis: { title: { text: '' }, labels: axisLabels(input, metric) },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      outside: true,
      formatter() {
        const context = this as unknown as PointContext
        return buildChartTooltip(context.point.category, [
          {
            color: context.color,
            label: chartFieldLabel(input, metric),
            value: formatChartValue(input, context.y, metric),
          },
          {
            color: 'var(--ui-color-fg)',
            label: chartFieldLabel(input, target),
            value: formatChartValue(input, context.point.target, target),
          },
        ])
      },
    },
    plotOptions: { bullet: { targetOptions: { width: '140%' } } },
    series: [
      {
        type: 'bullet',
        name: chartFieldLabel(input, metric),
        data: input.rows.map((row) => ({
          y: finiteNumber(row[metric]),
          target: finiteNumber(row[target]),
        })),
      },
    ],
    credits: { enabled: false },
  } as CategoricalChartOptions
}

/**
 * Diverging Bar: the breakdown dimension's two values become opposing legs over a shared category
 * axis (population-pyramid style). The first leg is negated for layout only — labels, axis ticks and
 * tooltips always show magnitudes. Any breakdown with other than two values cannot diverge.
 */
function buildDivergingBar(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  const dimension = firstField(fields, ['dimension'])
  const breakdown = firstField(fields, ['breakdown'])
  const metric = firstField(fields, ['metric'])
  if (!dimension || !breakdown || !metric || dimension === breakdown) return null
  const legs = uniqueCategories(input.rows, breakdown)
  if (legs.length !== 2) return null

  const categories = uniqueCategories(input.rows, dimension)
  const valueAt = (leg: string, name: string): number =>
    input.rows
      .filter((row) => category(row[breakdown]) === leg && category(row[dimension]) === name)
      .reduce((sum, row) => sum + finiteNumber(row[metric]), 0)

  return {
    chart: { type: 'bar', height: Math.max(360, categories.length * 28 + 120) },
    title: { text: '' },
    xAxis: [
      { categories, reversed: false },
      { categories, opposite: true, reversed: false, linkedTo: 0 },
    ],
    yAxis: {
      title: { text: chartFieldLabel(input, metric) },
      labels: {
        formatter(this: { value: number }) {
          return formatChartAxisValue(input, Math.abs(Number(this.value)), metric)
        },
      },
    },
    tooltip: {
      useHTML: true,
      outside: true,
      formatter() {
        const context = this as unknown as PointContext
        return buildChartTooltip(context.point.category, [
          {
            color: context.color,
            label: context.series.name,
            value: formatChartValue(input, Math.abs(Number(context.y)), metric),
          },
        ])
      },
    },
    plotOptions: { series: { stacking: 'normal', borderRadius: 4 } },
    series: legs.map((leg, index) => ({
      type: 'bar',
      name: leg,
      data: categories.map((name) => (index === 0 ? -1 : 1) * valueAt(leg, name)),
    })),
    credits: { enabled: false },
  } as CategoricalChartOptions
}

export function buildComparativeChartOptions(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  if (input.type === 'multi_metric') return buildMultiMetric(input, fields)
  if (input.type === 'dumbbell') return buildDumbbell(input, fields)
  if (input.type === 'bullet') return buildBullet(input, fields)
  if (input.type === 'diverging_bar') return buildDivergingBar(input, fields)
  return null
}
